/**
 * Generic Error Boundary Component
 *
 * Catches React errors in child components to prevent full app crashes
 * Provides customizable fallback UI and error reporting
 */

import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { logger } from '../../lib/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  componentName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const componentName = this.props.componentName || 'Unknown';

    // Always log to console (even in production) for debugging
    console.error(`[ErrorBoundary] Error in ${componentName}:`, error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    logger.error('ErrorBoundary', `Error in ${componentName}:`, error);
    logger.error('ErrorBoundary', 'Component stack:', errorInfo.componentStack);

    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // TODO: Send to error reporting service (e.g., Sentry)
    // reportError(error, { componentName, ...errorInfo });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      const componentName = this.props.componentName || 'this section';

      return (
        <div className="min-h-[200px] flex items-center justify-center p-8 bg-red-50 dark:bg-red-900/10 rounded-lg border-2 border-red-200 dark:border-red-800">
          <div className="max-w-md text-center">
            {/* Error Icon */}
            <div className="mb-4">
              <svg
                className="w-12 h-12 mx-auto text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            {/* Error Message */}
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Something went wrong
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-6">
              An error occurred in {componentName}. Your data is safe.
            </p>

            {/* Error Details (dev/alpha only) */}
            {this.state.error && import.meta.env.DEV && (
              <details className="mb-6 text-left bg-white dark:bg-gray-800 p-4 rounded border border-gray-300 dark:border-gray-600">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">
                  Error Details (Development)
                </summary>
                <pre className="mt-2 text-xs text-red-600 dark:text-red-400 overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {this.state.errorInfo && `\n\n${this.state.errorInfo.componentStack}`}
                </pre>
              </details>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 bg-sky-blue text-white rounded-lg hover:bg-sky-blue/90 transition-colors font-medium"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                Refresh Page
              </button>
            </div>

            {/* Help Text */}
            <p className="mt-6 text-xs text-gray-600 dark:text-gray-400">
              If this error persists, try refreshing or{' '}
              <a
                href="mailto:dan.tolan@gmail.com"
                className="text-sky-blue hover:underline"
              >
                contact support
              </a>
              .
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
